import type { ReactNode } from "react";
import type { DirectoryDataFileItem } from "@ServerTypes/fileOperations/fileOperations.schemas";
import type { FolderPermission } from "@ServerTypes/folders/folders.schema";
import type { SharingContentProps } from "./SharingContent/SharingContent";

export type { SharingContentProps };

/**
 * Props for `<DetailsContent />`. Annotating {@link buildDetailsContentProps} with this interface
 * makes the builder type-checked — a typo'd or wrong-typed key is now a compile error instead of
 * silently flowing through `DetailsContent(props: any)` and breaking at runtime. Field types mirror
 * the {@link DirectoryDataFileItem} row the builder reads from.
 */
export interface DetailsContentProps {
  disableEngineType?: boolean;
  resourceType?: string;
  resourceId?: string;
  name?: string;
  containerType?: string;
  engineType?: string;
  userName?: string;
  revision?: string;
  fileVersionId?: string;
  transmittalVersionId?: string;
  folderCreatedDate?: number | string;
  folderModifiedDate?: number | string;
  folderSize?: number;
  storage?: number;
  conversionStatus?: string;
  indexAtProjectLevel?: number | string;
  projectCustomId?: string | number;
  accessPermission?: FolderPermission | null;
  transmittalHasFileId?: string;
  fileId?: string;
  getUserAvatar: (id: string) => ReactNode;
  isFile?: boolean | number;
  path?: string;
  parentIdArray?: string[];
  spaceName?: string;
  spaceId?: string;
  projectId?: string;
  id?: string;
}

/**
 * Shared prop builders for the file-properties content panels.
 *
 * Both the full {@link FileVersion} panel and the lightweight transmittal-references
 * {@link TransmittedFileProperties} panel render the same `DetailsContent` / `SharingContent`
 * children with the same ~25-prop wiring. Centralising that mapping here keeps the two call sites
 * in sync and removes the block duplication that previously drove up the SonarQube duplication
 * metric.
 *
 * `file` is a single directory-data row (`getDirectoryDataOfFile` element); `ctx` carries the
 * values that come from the host component rather than the file row.
 */

interface SharingContentPropsContext {
  /** Transmittal `_id` when viewing in a transmittal context; falls back to the row's own id. */
  transmittalId?: string;
}

interface DetailsContentPropsContext extends SharingContentPropsContext {
  disableEngineType?: boolean;
  /** Resolved fallback project id (the host's `projectData.id`). */
  projectId?: string;
  getUserAvatar: (id: string) => ReactNode;
}

/** Builds the props for `<DetailsContent />`, identical across the two host panels. */
export function buildDetailsContentProps(
  file: DirectoryDataFileItem,
  ctx: DetailsContentPropsContext,
): DetailsContentProps {
  return {
    disableEngineType: ctx.disableEngineType,
    resourceType: file.resourceType,
    resourceId: file.hierarchyId,
    name: file.name,
    containerType: file.fileType,
    engineType: file.engineType,
    userName: file.userName,
    revision: file.revision?.value,
    fileVersionId: file.fileVersion,
    transmittalVersionId: ctx.transmittalId ?? file.transmittalVersionId,
    folderCreatedDate: file.dateCreated,
    folderModifiedDate: file.dateModified,
    folderSize: file.size,
    storage: file.storage,
    conversionStatus: file.conversionStatus,
    indexAtProjectLevel: file.indexAtProjectLevel,
    projectCustomId: file.projectCustomId,
    accessPermission: file.permission,
    transmittalHasFileId: file.transmittalHasFileId,
    fileId: file.hierarchyId,
    getUserAvatar: ctx.getUserAvatar,
    isFile: file.isFile,
    path: file.path,
    parentIdArray: file.parentIdArray,
    spaceName: file.space?.name,
    spaceId: file.spaceId,
    projectId: file.project?._id || ctx.projectId,
    id: file._id,
  };
}

/** Builds the props for `<SharingContent />`, identical across the two host panels. */
export function buildSharingContentProps(
  file: DirectoryDataFileItem,
  ctx: SharingContentPropsContext,
): SharingContentProps {
  return {
    resourceType: file.resourceType ?? "",
    resourceId: file.hierarchyId ?? "",
    fileVersionId: file.fileVersion,
    transmittalVersionId: ctx.transmittalId ?? file.transmittalVersionId,
    workspaceId: file.spaceId,
  };
}
